import _ = require("lodash");
import {
  ExcelColorValue,
  CellIndexMapping,
  CommonConfiguration,
  Constants,
  AddClientRequest,
  UploadStatus,
  CommonColumn,
} from "../../util/UIConstants";
import { EntityConfiguration } from "../../configurations/configuration";
import { ServiceDataModel } from "../models/Models";
import { ExcelHelper } from "../../helpers/ExcelHelper";
import { labels, currentResource, getFormattedDate } from "../../util/utils";
import { ExcelUtilityHelper } from "../../helpers/common/ExcelUtilityHelper";
import { TemplateUIExcelInterface } from "../template/TemplateUIExcelInterface";

export class ClientRequestUIExcelInterface extends TemplateUIExcelInterface {
  public static formatData(data, subHeadersDetails) {
    let finalValues = [];
    data.forEach((item) => {
      let values = [];
      subHeadersDetails.forEach((subHeader) => {
        values.push(subHeader.getCellData(item));
      });
      finalValues.push(values);
    });
    return finalValues;
  }

  private static async trackDuplicateData(handleDuplicateObjects: any, key) {
    // for now no need to track duplicate data, we will show only one item in dropdw
    if (handleDuplicateObjects[key]) {
      handleDuplicateObjects[key] = handleDuplicateObjects[key];
    } else {
      handleDuplicateObjects[key] = key;
    }
  }

  public static async getformatedClientRequestAPIResponse(responseArray: any, ldcVersioning) {
    const clientGroups = responseArray[0]?.data?.clientGroups;
    let handleDuplicateObjects = {};
    const clientGroupsItems = clientGroups
      ?.filter((clientGroup) => clientGroup?.userCount > 0)
      ?.map((clientGroup) => {
        this.trackDuplicateData(handleDuplicateObjects, clientGroup.groupName);
        return {
          group: `${clientGroup.groupName} (${clientGroup.groupId})`,
          groupId: clientGroup.groupId,
          groupName: handleDuplicateObjects[clientGroup.groupName],
        };
      })
      .sort((a, b) => a.groupName.localeCompare(b.groupName));

    const externalUsers = responseArray[1]?.data?.users;

    const externalUsersTempItems: any[] = [];
    const externalUsersAllNames: string[] = [];

    externalUsers?.forEach((externalUser) => {
      externalUser.groups
        .filter((group) => group.isActive)
        .forEach((group) => {
          const name = `${group.groupName}: ${externalUser.firstName} ${externalUser.lastName}`;
          externalUsersAllNames.push(name);
          externalUsersTempItems.push({
            userId: externalUser.userId,
            name: `${externalUser.firstName} ${externalUser.lastName}`,
            groupId: group.groupID,
            groupName: group.groupName,
            groupMemberName: name,
            email: externalUser.emailAddress,
          });
        });
    });

    // To check the occurrences of each name
    const externalUsersNameCount = _.countBy(externalUsersAllNames);

    //Map the Data again and append the email if the name occurs more than once
    const groupMemberItems = externalUsersTempItems.map((item) => ({
      userId: item.userId,
      name: item.name,
      groupId: item.groupId,
      groupName: item.groupName,
      groupMember:
        externalUsersNameCount[item.groupMemberName] > 1 ? `${item.groupMemberName} (${item.email})` : item.groupMemberName,
    })).sort((a, b) => a.groupName.localeCompare(b.groupName));

    const internalUsers = responseArray[2]?.data?.users;

    const tempInternalUsersItems: any[] = [];
    const allInternalUsersNames: string[] = [];

    internalUsers?.forEach((internalUser) => {
      // Clean name if it contains ';'
      let name = `${internalUser.firstName} ${internalUser.lastName}`.replace(/;/g, "_");
      allInternalUsersNames.push(name);
      tempInternalUsersItems.push({
        userId: internalUser.userId,
        name: name,
        email: internalUser.emailAddress,
      });
    });

    const internalUsersNameCount = _.countBy(allInternalUsersNames);

    const internalUsersItems = tempInternalUsersItems
      .map((item) => ({
        userId: item.userId,
        name: internalUsersNameCount[item.name] > 1 ? `${item.name} (${item.email})` : item.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    handleDuplicateObjects = {};
    const tags = responseArray[3]?.data?.data;
    const tagsItems =
      tags && tags.length > 0
        ? tags
            .map((tag) => {
              let colorCode = currentResource.colorOptionsLabels.find((color) => color.value == tag.colorCode)?.label;
              const updatedTagName = tag.engagementTagName.includes(";")
                ? tag.engagementTagName.replace(/;/g, "_")
                : tag.engagementTagName;
              let name = `${updatedTagName} (${colorCode ? colorCode : tag.colorCode})`;
              this.trackDuplicateData(handleDuplicateObjects, name);
              return {
                tagName: handleDuplicateObjects[name],
                colorCode: colorCode,
                tagId: tag.engagementTagId,
                engagementTagName: updatedTagName,
              };
            })
            .sort((a, b) => a.tagName.localeCompare(b.tagName))
        : [];

    const timePhasestypes = responseArray[4]?.data;
    handleDuplicateObjects = {};

    const timePhasesItems = timePhasestypes?.flatMap((timePhaseType) =>
      timePhaseType.timePhases.map((timePhase) => {
        let name = `${timePhase.periodName}`;
        this.trackDuplicateData(handleDuplicateObjects, name);
        return {
          timePhaseName: handleDuplicateObjects[name],
          timePhaseId: timePhase.id,
          timePhase: timePhase.periodName,
          startDate: timePhase.startDate ? getFormattedDate(timePhase.startDate?.split("T")[0], 3) : "",
          dueDate: timePhase.dueDate ? getFormattedDate(timePhase.dueDate?.split("T")[0], 3) : "",
        };
      })
    );

    const priorityTypes = currentResource.priorityTypesCollaboration.map((priorityType) => ({
      priorityTypeId: priorityType.value,
      priorityTypeName: priorityType.label,
    }));

    return {
      priorityTypesData: priorityTypes,
      clientGroupsData: clientGroupsItems,
      internalUsersData: internalUsersItems,
      externalUsersData: groupMemberItems,
      tagsData: tagsItems,
      timePhasesData: timePhasesItems,
    };
  }

  adjustColumnsWidth(sheet: Excel.Worksheet, configuration: EntityConfiguration) {
    const headers = configuration.referenceSheetColumns;
    headers.forEach((header) => {
      let subHeadersDetails = header.subColumns;
      if (header.adjustColumnWidth) {
        subHeadersDetails.forEach((subHeader) => {
          if (subHeader.CellFormat.columnWidth) {
            let column = CellIndexMapping[subHeader.CellDisplayIndex];
            let columnRange = sheet.getRange(`${column}:${column}`);
            columnRange.format.columnWidth = subHeader.CellFormat.columnWidth;
          }
        });
      }
    });
  }

  async populateReferenceSheetAsync(
    rowsDataModel: ServiceDataModel,
    masterSheet: Excel.Worksheet,
    context: Excel.RequestContext,
    masterSheetName: string,
    configuration: any
  ): Promise<any> {
    // not require any additional data in reference sheet
    const referenceData = rowsDataModel.referenceData;
    const headers = TemplateUIExcelInterface.updateReferenceSheetHeaders(configuration.referenceSheetColumns);
    const headerDataMap = {
      isPriorityHeader: "priorityTypesData",
      isGroupsHeader: "clientGroupsData",
      isExternalUsers: "externalUsersData",
      isInternalUsers: "internalUsersData",
      isTagsHeader: "tagsData",
      isTimePhasesHeader: "timePhasesData",
    };
    let columnRanges = [];

    headers.forEach((header, i) => {
      const startColumn = CellIndexMapping[header.headerRangeStartIndex];
      const endColumn = CellIndexMapping[header.headerRangeLastIndex];
      let headerRange: Excel.Range = masterSheet.getRange(`${startColumn}3:${endColumn}3`);
      const totalCells = header.headerRangeLastIndex - header.headerRangeStartIndex + 1;
      let headerValues = new Array(totalCells).fill("");
      headerValues[0] = header.CellHeader;
      headerRange.values = [headerValues];
      headerRange.merge(true);
      headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = ExcelColorValue.GrayColor;

      let subHeadersDetails = header.subColumns; // Get the corresponding subheaders
      let subHeaders = subHeadersDetails.map((subHeader) => subHeader.CellHeader); // Create an array of subheader values

      if (!header.hasNoSubHeaders) {
        let subHeaderRange: Excel.Range = masterSheet.getRange(`$${startColumn}4:$${endColumn}4`);
        subHeaderRange.values = [subHeaders];
        subHeaderRange.format.font.bold = true;
        for (let key in headerDataMap) {
          if (header[key]) {
            const data = referenceData[headerDataMap[key]];
            const finalValues = ClientRequestUIExcelInterface.formatData(data, subHeadersDetails);
            if (finalValues.length > 0) {
              let dataValuesRange: Excel.Range = masterSheet.getRange(
                `$${startColumn}5:$${endColumn}${finalValues.length + 4}`
              );
              dataValuesRange.numberFormat = [["@"]];
              dataValuesRange.values = finalValues;

              // to note down the drop down ranges
              let keyColumnIndex = CellIndexMapping[header.IdColumnIndex];
              let valueColumnIndex = CellIndexMapping[header.valueColumnIndex];

              columnRanges.push({
                [header.cellId]: {
                  keyColumn: keyColumnIndex,
                  IdRange: `${keyColumnIndex}5:${keyColumnIndex}${finalValues.length + 4}`,
                  valueColumn: valueColumnIndex,
                  valueAddressRange: `${valueColumnIndex}5:${valueColumnIndex}${finalValues.length + 4}`,
                },
              });
            }
          }
        }
      }
      if (i !== headers.length - 1) {
        let spaceColumn = CellIndexMapping[header.headerRangeLastIndex + 1];
        let spaceColumnRange = masterSheet.getRange(`${spaceColumn}3:${spaceColumn}3`);
        spaceColumnRange.format.columnWidth = 12;
      }
    });

    masterSheet.getUsedRange().format.autofitColumns();
    masterSheet.getUsedRange().format.autofitRows();
    let usedRange = masterSheet.getUsedRange();
    usedRange.format.wrapText = false;
    await ExcelHelper.tryCatchContextSync(context, `loaded master sheet with additional ref data`);
    await this.adjustColumnsWidth(masterSheet, configuration);

    return columnRanges;
  }

  getEndingDataExcelCellName = (columns) => {
    let excelCellName = null;
    let endColumn = _.sortBy(columns, ["CellDisplayIndex"]).reverse()[0];
    if (endColumn != null) {
      excelCellName = CellIndexMapping[endColumn.CellDisplayIndex];
    }

    return excelCellName;
  };

  async doChangesWhenChangingUpdateEYCanvas(
    context: Excel.RequestContext,
    sheet: Excel.Worksheet,
    firstUpdatedRowIndex: number,
    lastUpdatedRowIndex: number,
    configuration: EntityConfiguration,
    endingDataColumnCellName: string,
    markForUpdateCellName: string,
    uploadStatusCellName: string,
    uploadStatusIdCellName: string,
    triggerTemplate?: boolean
  ): Promise<any> {
    if (triggerTemplate) {
      super.doChangesWhenChangingUpdateEYCanvas(
        context,
        sheet,
        firstUpdatedRowIndex,
        lastUpdatedRowIndex,
        configuration,
        endingDataColumnCellName,
        markForUpdateCellName,
        uploadStatusCellName,
        uploadStatusIdCellName
      );
    }
    for (let i: number = firstUpdatedRowIndex; i <= lastUpdatedRowIndex; i++) {
      let updateRowRange = sheet.getRange(`${CommonColumn.BeginningColumn}${i}:${endingDataColumnCellName}${i}`);
      updateRowRange.load("values");
      await ExcelHelper.tryCatchContextSync(context, `mark for update load ${markForUpdateCellName}${i}`);
      const markForUpdateValue = updateRowRange.values.at(0)?.[CommonColumn.markUpdateValue];
      const title = updateRowRange.values.at(0)?.[AddClientRequest.ClientRequestTitle].toString().trim();

      if (_.isEqual(Constants.MarkForUpdateYes, markForUpdateValue)) {
        if (title) {
          await ExcelUtilityHelper.doMarkForUpdateRangeFormatChanges(
            context,
            sheet,
            firstUpdatedRowIndex,
            lastUpdatedRowIndex,
            CommonColumn.BeginningColumn,
            endingDataColumnCellName,
            configuration
          );
          ExcelUtilityHelper.doUploadStatusValueAndFormatChanges(
            sheet,
            i,
            UploadStatus.Pending,
            uploadStatusCellName,
            uploadStatusIdCellName
          );
        } else {
          await ExcelUtilityHelper.doMarkForUpdateRangeFormatChanges(
            context,
            sheet,
            firstUpdatedRowIndex,
            lastUpdatedRowIndex,
            CommonColumn.BeginningColumn,
            endingDataColumnCellName,
            configuration,
            ExcelColorValue.Red
          );
          ExcelUtilityHelper.doUploadStatusValueAndFormatChanges(
            sheet,
            i,
            labels.clientRequestTitleMissing,
            uploadStatusCellName,
            uploadStatusIdCellName,
            markForUpdateCellName
          );
        }
      } else if (_.isEqual(Constants.MarkForUpdateNo, markForUpdateValue)) {
        await ExcelUtilityHelper.doMarkForUpdateRangeFormatChanges(
          context,
          sheet,
          firstUpdatedRowIndex,
          lastUpdatedRowIndex,
          CommonColumn.BeginningColumn,
          endingDataColumnCellName,
          configuration,
          ""
        );
        ExcelUtilityHelper.doUploadStatusValueAndFormatChanges(
          sheet,
          i,
          "",
          uploadStatusCellName,
          uploadStatusIdCellName
        );
      }
      // else, do nothing
    }
    await ExcelHelper.tryCatchContextSync(
      context,
      `single cell update mark row yellow dirty fromRow: ${firstUpdatedRowIndex} : toRow: ${lastUpdatedRowIndex}`
    );
  }

  async applyFormattingOnCellChange(
    activeSheet: Excel.Worksheet,
    beginDataRowIndex: number,
    endDataRowIndex: number,
    configuration: EntityConfiguration,
    requestsModel: ServiceDataModel
  ): Promise<any> {}
}
